import React, { useState, useEffect } from "react";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { BRAND, whatsappLink } from "../lib/constants";
import Logo from "./Logo";

const links = [
    { href: "#estate", label: "Estate" },
    { href: "#pool", label: "Pool" },
    { href: "#dining", label: "Dining" },
    { href: "#features", label: "Activities" },
    { href: "#rooms", label: "Stays" },
    { href: "#events", label: "Events" },
    { href: "#reviews", label: "Reviews" },
    { href: "#contact", label: "Contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-cream/90 backdrop-blur-md border-b border-sand shadow-sm"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10 py-4 flex items-center justify-between">
                <a href="#home" className="flex items-center gap-3 shrink-0" data-testid="navbar-logo">
                    <Logo size={44} />
                    <div className="flex flex-col leading-none whitespace-nowrap">
                        <span className={`font-serif text-[13px] sm:text-base md:text-lg tracking-[0.18em] ${scrolled ? "text-coffee-800" : "text-cream"}`}>
                            VEERAS <span className="italic font-medium text-[#B98E4E]">WESTERN GHATS</span>
                        </span>
                        <span className={`text-[8px] sm:text-[9px] md:text-[10px] tracking-[0.32em] uppercase mt-1.5 ${scrolled ? "text-coffee-500" : "text-cream/85"}`}>
                            Resorts &amp; Hospitality
                        </span>
                    </div>
                </a>

                <nav className="hidden xl:flex items-center gap-7">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={`text-sm tracking-wide whitespace-nowrap hover:text-forest-500 transition-colors ${
                                scrolled ? "text-coffee-700" : "text-cream/90"
                            }`}
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href={`tel:${BRAND.phone}`}
                        data-testid="navbar-call-btn"
                        className={`hidden lg:inline-flex items-center gap-2 text-sm whitespace-nowrap ${scrolled ? "text-coffee-700" : "text-cream"}`}
                    >
                        <Phone className="h-4 w-4" /> {BRAND.phoneIntl}
                    </a>
                    <a
                        href={whatsappLink()}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="navbar-whatsapp-btn"
                        className="hidden sm:inline-flex items-center gap-2 bg-[#25D366] text-white px-4 py-2.5 rounded-full text-sm font-medium shadow hover:-translate-y-0.5 transition-transform whitespace-nowrap"
                    >
                        <MessageCircle className="h-4 w-4" /> Book on WhatsApp
                    </a>
                    <button
                        aria-label="Open menu"
                        onClick={() => setOpen(!open)}
                        className={`xl:hidden p-2 ${scrolled ? "text-coffee-800" : "text-cream"}`}
                        data-testid="navbar-mobile-toggle"
                    >
                        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="xl:hidden bg-cream border-t border-sand" data-testid="navbar-mobile-menu">
                    <div className="px-6 py-4 flex flex-col gap-4">
                        {links.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={() => setOpen(false)}
                                className="text-coffee-800 text-base"
                                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href={whatsappLink()}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-4 py-3 rounded-full text-sm font-medium"
                        >
                            <MessageCircle className="h-4 w-4" /> Book on WhatsApp
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
